import React, { useState, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Table,
  Button,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  Plus,
  Trash2,
  Download,
  Eye,
  Calculator,
  Calendar,
  Percent,
} from "lucide-react";
import logo from "../tn-logo.png";

const CPSStatementGenerator = () => {
  const [employeeDetails, setEmployeeDetails] = useState({
    name: "",
    cpsNo: "",
    designation: "",
    officeOf: "",
    dateOfBirth: "",
    dateOfJoining: "",
    interestRate: 0.0,
  });
  const currentYear = new Date().getFullYear()
  const [contributions, setContributions] = useState([
    {
      id: 1,
      month: "",
      year: new Date().getFullYear(),
      contribution: 0,
      arrear: 0,
      others: 0,
      interestRate: 0.0,
    },
  ]);

  const [openingBalance, setOpeningBalance] = useState({
    contribution: 0,
    arrear: 0,
    others: 0,
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showRangeModal, setShowRangeModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [rangeData, setRangeData] = useState({
    fromMonth: "MAR",
    fromYear: currentYear,
    toMonth: "FEB",
    toYear: currentYear + 1,
    monthlyContribution: 0,
    monthlyArrear: 0,
    monthlyOthers: 0,
    interestRate: 0.0,
  });
  const [bulkInterestData, setBulkInterestData] = useState({
    fromMonth: rangeData.fromMonth,
    fromYear: rangeData.fromYear,
    toMonth: rangeData.toMonth,
    toYear: rangeData.toYear,
    interestRate: 0.0,
  });
  const [errors, setErrors] = useState({});
  const printRef = useRef();

  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const handleClear = () => {
    setContributions([
      {
        id: 1,
        month: "",
        year: new Date().getFullYear(),
        contribution: 0,
        arrear: 0,
        others: 0,
        interestRate: 0.0,
      },
    ])
    setRangeData({
      fromMonth: "MAR",
      fromYear: currentYear,
      toMonth: "FEB",
      toYear: currentYear + 1,
      monthlyContribution: 0,
      monthlyArrear: 0,
      monthlyOthers: 0,
      interestRate: 0.0,
    });
    setBulkInterestData({
      fromMonth: rangeData.fromMonth,
      fromYear: rangeData.fromYear,
      toMonth: rangeData.toMonth,
      toYear: rangeData.toYear,
      interestRate: 0.0,
    })
  }
  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate employee details
    if (!employeeDetails.name.trim()) newErrors.name = "Name is required";
    if (!employeeDetails.cpsNo.trim()) newErrors.cpsNo = "CPS No is required";
    if (!employeeDetails.designation.trim())
      newErrors.designation = "Designation is required";
    if (!employeeDetails.dateOfBirth)
      newErrors.dateOfBirth = "Date of Birth is required";
    if (!employeeDetails.dateOfJoining)
      newErrors.dateOfJoining = "Date of Joining is required";

    // Validate contributions
    contributions.forEach((contrib, index) => {
      if (!contrib.month) newErrors[`month_${index}`] = "Month is required";
      if (contrib.contribution < 0)
        newErrors[`contribution_${index}`] = "Cannot be negative";
      if (contrib.arrear < 0)
        newErrors[`arrear_${index}`] = "Cannot be negative";
      if (contrib.others < 0)
        newErrors[`others_${index}`] = "Cannot be negative";
      if (contrib.interestRate < 0)
        newErrors[`interestRate_${index}`] = "Interest rate cannot be negative";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [employeeDetails, contributions]);

  const handleEmployeeDetailChange = (field, value) => {
    setEmployeeDetails((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleContributionChange = (id, field, value) => {
    const index = contributions.findIndex((c) => c.id === id);
    let numericValue;
    if (field === "month" || field === "year") {
      numericValue = value;
    } else {
      numericValue = Math.max(0, parseFloat(value) || 0);
    }
    setContributions((prev) =>
      prev.map((contrib) =>
        contrib.id === id ? { ...contrib, [field]: numericValue } : contrib
      )
    );
    if (errors[`${field}_${index}`]) {
      setErrors((prev) => ({ ...prev, [`${field}_${index}`]: null }));
    }
  };

  const addContribution = () => {
    const newId =
      contributions.length > 0
        ? Math.max(...contributions.map((c) => c.id)) + 1
        : 1;
    setContributions((prev) => [
      ...prev,
      {
        id: newId,
        month: "",
        year: new Date().getFullYear(),
        contribution: 0,
        arrear: 0,
        others: 0,
        interestRate: employeeDetails.interestRate,
      },
    ]);
  };

  const removeContribution = (id) => {
    if (contributions.length > 1) {
      setContributions((prev) => prev.filter((contrib) => contrib.id !== id));
    }
  };

  const calculateRowTotal = (contrib) => {
    return contrib.contribution + contrib.arrear + contrib.others;
  };

  const calculateGrandTotals = () => {
    const totals = contributions.reduce(
      (acc, contrib) => ({
        contribution: acc.contribution + contrib.contribution,
        arrear: acc.arrear + contrib.arrear,
        others: acc.others + contrib.others,
        total: acc.total + calculateRowTotal(contrib),
      }),
      { contribution: 0, arrear: 0, others: 0, total: 0 }
    );

    return totals;
  };
  const getMonthIndex = (month) => months.indexOf(month.toUpperCase());

  const calculateMonthlyInterest = (principal, annualInterestRate) => {
    const monthlyRate = (annualInterestRate || 8) / 12 / 100;
    return principal * monthlyRate;
  };

  const calculateSummary = () => {
    const sortedContributions = contributions
      .filter((c) => c.month && c.year)
      .map((c) => {
        const monthIndex = getMonthIndex(c.month);
        const total = c.contribution + c.arrear + c.others;
        return { ...c, monthIndex, total };
      })
      .sort((a, b) =>
        a.year !== b.year ? a.year - b.year : a.monthIndex - b.monthIndex
      );

    // Opening balance total
    const openingTotal =
      openingBalance.contribution +
      openingBalance.arrear +
      openingBalance.others;

    // Sum of all contributions (without interest)
    const creditDetailsTotal = sortedContributions.reduce(
      (sum, c) => sum + c.total,
      0
    );
    if (sortedContributions.length === 0) {
      return {
        openingBalance: Math.round(openingTotal),
        deposits: Math.round(creditDetailsTotal),
        interest: 0,
        closingBalance: Math.round(openingTotal),
        boardTotal: Math.round(openingTotal * 2),
        interestRanges: [],
        monthlyBreakdown: [],
      };
    }
    // Find the last year and month to calculate interest till that point
    const lastContribution =
      sortedContributions[sortedContributions.length - 1];
    const lastYear = lastContribution.year;
    const lastMonthIndex = lastContribution.monthIndex;

    const openingYear = openingBalance.year || sortedContributions[0].year;
    const openingMonthIndex =
      openingBalance.monthIndex || sortedContributions[0].monthIndex;

    // Calculate months difference between opening balance start and last contribution month
    const openingMonthsCount =
      (lastYear - openingYear) * 12 + (lastMonthIndex - openingMonthIndex) + 1;

    // Calculate opening balance interest month-wise
    let totalInterestOpeningBalance = 0;
    let monthIndex = openingMonthIndex;
    let year = openingYear;

    for (let i = 0; i < openingMonthsCount; i++) {
      const currentMonth = months[monthIndex];
      const monthYearStr = `${currentMonth}-${year}`;

      // Get dynamic interest rate for this month-year
      const interestRateForMonth = getInterestRateForMonth(
        monthYearStr,
        getInterestRanges()
      );

      // Calculate monthly interest for this month and add it to total
      const interestForMonth = calculateMonthlyInterest(
        openingTotal,
        interestRateForMonth
      );
      totalInterestOpeningBalance += interestForMonth;

      monthIndex++;
      if (monthIndex > 11) {
        monthIndex = 0;
        year++;
      }
    }

    // Initialize totalInterest with opening balance interest
    let totalInterest = totalInterestOpeningBalance;

    const monthlyInterestLog = [];

    // For each contribution, calculate month-wise interest compounded until last contribution month
    sortedContributions.forEach((contrib, contribIndex) => {
      let principal = contrib.total;
      let year = contrib.year;
      let monthIndex = contrib.monthIndex;
      const originalPrincipal = principal;

      console.log(
        `\n🧾 Contribution #${contribIndex + 1} — ${contrib.month} ${contrib.year
        }`
      );
      console.log(
        `➡️ Fixed Starting Principal (Original Contribution): ${originalPrincipal}`
      );
      console.log(
        `📅 Calculating from ${months[monthIndex]} ${year} to ${months[lastMonthIndex]} ${lastYear}`
      );

      while (
        year < lastYear ||
        (year === lastYear && monthIndex <= lastMonthIndex)
      ) {
        const currentMonth = months[monthIndex];
        const interestRate = getInterestRateForMonth(
          `${currentMonth}-${year}`,
          getInterestRanges()
        );

        console.log("const interestRate", interestRate);

        const interest = calculateMonthlyInterest(
          originalPrincipal,
          interestRate
        ); // ✅ fixed

        const isContributionMonth =
          monthIndex === contrib.monthIndex && year === contrib.year;
        const contributionAmount = isContributionMonth
          ? parseFloat(originalPrincipal.toFixed(2))
          : 0;

        console.log(`\n🗓️ ${currentMonth} ${year}`);
        console.log(`   ➤ Opening Balance: ${originalPrincipal.toFixed(5)}`); // ✅ not principal
        console.log(
          `   ➤ Interest Rate: ${interestRate}% annually → ${(
            interestRate / 12
          ).toFixed(3)}% monthly`
        );
        console.log(`   ➤ Monthly Interest: ${interest.toFixed(5)}`);
        if (isContributionMonth) {
          console.log(
            `   ➤ Original Contribution in this month: ${contributionAmount}`
          );
        }
        totalInterest += interest;
        monthlyInterestLog.push({
          month: currentMonth,
          year: year,
          interestRate,
          openingBalance: parseFloat(originalPrincipal.toFixed(5)),
          interest: parseFloat(interest.toFixed(5)),
          contribution: contributionAmount,
          closingBalance: parseFloat((originalPrincipal + interest).toFixed(5)), // optional
        });

        // ❌ Don't update principal anymore
        // principal = originalPrincipal + interest;  ← REMOVE THIS

        // Move to next month
        monthIndex++;
        if (monthIndex > 11) {
          monthIndex = 0;
          year++;
        }
      }

      const compoundInterest = principal - originalPrincipal;
      console.log(
        `\n📌 Compound Interest earned on this contribution: ${compoundInterest.toFixed(
          5
        )}`
      );
      totalInterest += compoundInterest;
    });

    // Final closing balance includes opening total + contributions + total interest
    const finalClosingBalance =
      openingTotal + creditDetailsTotal + totalInterest;
    const boardTotal = finalClosingBalance * 2;

    const summary = {
      openingBalance: Math.round(openingTotal),
      deposits: Math.round(creditDetailsTotal),
      interest: Math.round(totalInterest),
      closingBalance: Math.round(finalClosingBalance),
      boardTotal: Math.round(boardTotal),
      interestRanges: getInterestRanges(),
      monthlyBreakdown: monthlyInterestLog,
    };

    console.log("📊 Opening Balance:", openingTotal);
    console.log("💰 Credit Details Total:", creditDetailsTotal);
    console.log("📈 Total Interest:", totalInterest);
    console.log("🏁 Final Closing Balance:", finalClosingBalance);
    console.table(monthlyInterestLog);
    console.log("✅ Final Summary:", summary);

    console.log("getInterestRanges()", getInterestRanges());

    return summary;
  };

  function getInterestRateForMonth(monthYear, interestRanges) {
    const [monthStr, yearStr] = monthYear.split("-");
    const monthIndex = new Date(Date.parse(monthStr + " 1, 2000")).getMonth();
    const monthDate = new Date(`${yearStr}-${monthIndex + 1}-01`);

    for (const range of interestRanges) {
      const [startMonthStr, startYearStr] = range.start.split("-");
      const [endMonthStr, endYearStr] = range.end.split("-");

      const startDate = new Date(
        `${startYearStr}-${new Date(Date.parse(startMonthStr + " 1, 2000")).getMonth() + 1
        }-01`
      );
      const endDate = new Date(
        `${endYearStr}-${new Date(Date.parse(endMonthStr + " 1, 2000")).getMonth() + 1
        }-01`
      );

      if (monthDate >= startDate && monthDate <= endDate) {
        return range.rate;
      }
    }

    return 8; // default
  }

  const getInterestRanges = () => {
    const ranges = [];
    let currentRate = null;
    let rangeStart = null;

    const sortedContribs = [...contributions]
      .filter((c) => c.month && c.year)
      .sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return months.indexOf(a.month) - months.indexOf(b.month);
      });

    sortedContribs.forEach((contrib, index) => {
      if (currentRate !== contrib.interestRate) {
        if (currentRate !== null && rangeStart !== null) {
          const prevContrib = sortedContribs[index - 1];
          ranges.push({
            rate: currentRate,
            start: rangeStart,
            end: `${prevContrib.month}-${prevContrib.year}`,
          });
        }
        currentRate = contrib.interestRate;
        rangeStart = `${contrib.month}-${contrib.year}`;
      }
    });

    if (currentRate !== null && rangeStart !== null) {
      const lastContrib = sortedContribs[sortedContribs.length - 1];
      ranges.push({
        rate: currentRate,
        start: rangeStart,
        end: `${lastContrib.month}-${lastContrib.year}`,
      });
    }
    console.log(ranges);

    return ranges;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  const handlePrint = () => {
    if (!validateForm()) {
      alert("Please fix all errors before printing");
      return;
    }

    // Set document title for print filename
    const originalTitle = document.title;
    const employeeName = employeeDetails.name || "Employee";
    const currentYear = new Date().getFullYear();
    document.title = `CPS_Statement_${employeeName.replace(
      /\s+/g,
      "_"
    )}_${currentYear}`;

    setTimeout(() => {
      window.print();
      // Restore original title after printing
      document.title = originalTitle;
    }, 100);
  };

  const generateMonthRange = () => {
    if (!rangeData.fromMonth || !rangeData.toMonth) {
      alert("Please select both from and to months");
      return;
    }

    const fromMonthIndex = months.indexOf(rangeData.fromMonth);
    const toMonthIndex = months.indexOf(rangeData.toMonth);

    let startYear = rangeData.fromYear;
    let endYear = rangeData.toYear;

    // If to month is before from month in the same year, assume it's next year
    if (startYear === endYear && toMonthIndex < fromMonthIndex) {
      endYear = startYear + 1;
    }

    const newContributions = [];
    let currentYear = startYear;
    let currentMonthIndex = fromMonthIndex;
    let id = Math.max(...contributions.map((c) => c.id), 0) + 1;

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonthIndex <= toMonthIndex)
    ) {
      newContributions.push({
        id: id++,
        month: months[currentMonthIndex],
        year: currentYear,
        contribution: rangeData.monthlyContribution,
        arrear: rangeData.monthlyArrear,
        others: rangeData.monthlyOthers,
        interestRate: rangeData.interestRate,
      });

      currentMonthIndex++;
      if (currentMonthIndex >= 12) {
        currentMonthIndex = 0;
        currentYear++;
      }
    }

    setContributions(newContributions);
    setShowRangeModal(false);
  };

  const applyBulkInterestRate = () => {
    if (!bulkInterestData.fromMonth || !bulkInterestData.toMonth) {
      alert("Please select both from and to months");
      return;
    }

    const fromMonthIndex = months.indexOf(bulkInterestData.fromMonth);
    const toMonthIndex = months.indexOf(bulkInterestData.toMonth);
    let startYear = bulkInterestData.fromYear;
    let endYear = bulkInterestData.toYear;

    if (startYear === endYear && toMonthIndex < fromMonthIndex) {
      endYear = startYear + 1;
    }

    setContributions((prev) =>
      prev.map((contrib) => {
        const contribMonthIndex = months.indexOf(contrib.month);
        const contribYear = contrib.year;

        // Check if this contribution falls within the range
        const isInRange =
          (contribYear > startYear ||
            (contribYear === startYear &&
              contribMonthIndex >= fromMonthIndex)) &&
          (contribYear < endYear ||
            (contribYear === endYear && contribMonthIndex <= toMonthIndex));

        if (isInRange) {
          return { ...contrib, interestRate: bulkInterestData.interestRate };
        }
        return contrib;
      })
    );

    setShowInterestModal(false);
  };

  const StatementPreview = () => {
    const summary = calculateSummary();
    const grandTotals = calculateGrandTotals();

    return (
      <div
        ref={printRef}
        className="statement-preview p-3 bg-white"
        style={{
          fontSize: "12px",
          lineHeight: "1.2",
          border: "2px solid #808080",
        }}
      >
        {/* Header Section - Compact */}
        <div className="text-center mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div
              className="me-3"
              style={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={logo}
                alt="Tamil Nadu Pollution Control Board Logo"
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "contain",
                }}
              />
            </div>
            <div>
              <h6
                className="mb-1"
                style={{ fontSize: "14px", fontWeight: "bold" }}
              >
                TAMIL NADU POLLUTION CONTROL BOARD
              </h6>
              <div
                className="mb-1"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                CONTRIBUTION PENSION SCHEME STATEMENT FOR THE YEAR{" "}
                {summary.interestRanges.length >= 1 ? (() => {
                  const startYear = summary.interestRanges[0].start.slice(-4);
                  const endYear = summary.interestRanges[summary.interestRanges.length - 1].end.slice(-4);
                  return startYear === endYear ? startYear : `${startYear} - ${endYear}`;
                })() : ""}
              </div>
              <small style={{ fontSize: "10px" }}>
                {summary.interestRanges.length >= 1
                  ? summary.interestRanges.map((range, index) => (
                    <span key={index}>
                      Interest Rate @ {range.rate}% ({range.start} to{" "}
                      {range.end})
                      {index < summary.interestRanges.length - 1 && <br />}
                    </span>
                  ))
                  : `Interest Rate @ ${employeeDetails.interestRate}% per Annum`}
              </small>
            </div>
            <div></div>
          </div>
        </div>

        {/* Employee Details Table - Compact */}
        <Table
          bordered
          size="sm"
          className="mb-3"
          style={{ fontSize: "13px", border: "1px solid black" }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  padding: "10px 6px",
                  fontWeight: "bold",
                  width: "15%",
                }}
              >
                Name
              </td>
              <td style={{ padding: "10px 6px", width: "35%" }}>
                {employeeDetails.name}
              </td>
              <td
                style={{
                  padding: "10px 6px",
                  fontWeight: "bold",
                  width: "15%",
                }}
              >
                CPS AC No
              </td>
              <td style={{ padding: "4px 6px", width: "35%" }}>
                {employeeDetails.cpsNo}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "10px 6px", fontWeight: "bold" }}>
                Designation
              </td>
              <td style={{ padding: "10px 6px" }}>
                {employeeDetails.designation}
              </td>
              <td style={{ padding: "10px 6px", fontWeight: "bold" }}>
                Office Of
              </td>
              <td style={{ padding: "10px 6px" }}>
                {employeeDetails.officeOf}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "10px 6px", fontWeight: "bold" }}>
                Date of Birth
              </td>
              <td style={{ padding: "10px 6px" }}>
                {formatDate(employeeDetails.dateOfBirth)}
              </td>
              <td style={{ padding: "10px 6px", fontWeight: "bold" }}>
                Date of Joining
              </td>
              <td style={{ padding: "10px 6px" }}>
                {formatDate(employeeDetails.dateOfJoining)}
              </td>
            </tr>
          </tbody>
        </Table>

        {/* Contributions Table - Compact */}
        <Table
          bordered
          size="sm"
          className="mb-3"
          style={{ fontSize: "12px", border: "1px solid black" }}
        >
          <thead className="table-light" style={{ border: "1px solid black" }}>
            <tr className="text-center">
              <th
                rowSpan="2"
                style={{
                  padding: "4px 2px",
                  verticalAlign: "middle",
                  width: "12%",
                }}
              >
                Month & Year
              </th>
              <th colSpan="4" style={{ padding: "8px 2px" }}>
                CREDIT DETAILS
              </th>
              <th colSpan="2" style={{ padding: "8px 2px" }}>
                BOARD
              </th>
              {/* <th rowSpan="2" style={{ padding: '4px 2px', verticalAlign: 'middle', width: '8%' }}>Interest Rate</th> */}
            </tr>
            <tr className="text-center">
              <th style={{ padding: "6px 2px", width: "12%" }}>Contribution</th>
              <th style={{ padding: "6px 2px", width: "10%" }}>Arrear</th>
              <th style={{ padding: "6px 2px", width: "10%" }}>Others</th>
              <th style={{ padding: "6px 2px", width: "12%" }}>Total</th>
              <th style={{ padding: "6px 2px", width: "12%" }}>Contribution</th>
              <th style={{ padding: "6px 2px", width: "10%" }}>Others</th>
            </tr>
          </thead>
          <tbody>
            {contributions
              .filter((c) => c.month && c.year)
              .sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return months.indexOf(a.month) - months.indexOf(b.month);
              })
              .map((contrib) => (
                <tr key={contrib.id}>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {contrib.month}-{contrib.year}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {contrib.contribution.toFixed(0)}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {contrib.arrear.toFixed(0)}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {contrib.others.toFixed(0)}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {calculateRowTotal(contrib).toFixed(0)}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    {calculateRowTotal(contrib).toFixed(0)}
                  </td>
                  <td className="text-center" style={{ padding: "6px 2px" }}>
                    0
                  </td>
                  {/* <td className="text-center" style={{ padding: '3px 2px' }}>{contrib.interestRate}%</td> */}
                </tr>
              ))}
          </tbody>
        </Table>

        {/* Summary Table - Compact */}
        <Table
          bordered
          size="sm"
          className="mb-3"
          style={{ fontSize: "13px", border: "1px solid black" }}
        >
          <thead className="table-light" style={{ border: "1px solid black" }}>
            <tr className="text-center">
              <th
                rowSpan="2"
                style={{
                  padding: "8px 6px",
                  verticalAlign: "middle",
                  width: "20%",
                }}
              >
                Particulars
              </th>
              <th colSpan="4" style={{ padding: "8px 6px" }}>
                Employee Contribution
              </th>
              <th style={{ padding: "8px 6px", width: "15%" }}>Contribution</th>
              <th style={{ padding: "8px 6px", width: "15%" }}>Total</th>
            </tr>
            <tr className="text-center">
              <th style={{ padding: "6px 6px", width: "12%" }}>Contribution</th>
              <th style={{ padding: "6px 6px", width: "10%" }}>Arrear</th>
              <th style={{ padding: "6px 6px", width: "10%" }}>Others</th>
              <th style={{ padding: "6px 6px", width: "12%" }}>Total</th>
              <th style={{ padding: "6px 6px" }}></th>
              <th style={{ padding: "6px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "6px 6px", fontWeight: "bold" }}>
                Opening Balance
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {openingBalance.contribution.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.openingBalance.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.openingBalance.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {(summary.openingBalance * 2).toFixed(0)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "4px 6px", fontWeight: "bold" }}>
                Deposits
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {grandTotals.contribution.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {grandTotals.arrear.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {grandTotals.others.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.deposits.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.deposits.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {(summary.deposits * 2).toFixed(0)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "6px 6px", fontWeight: "bold" }}>
                Interest
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.interest.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {summary.interest.toFixed(0)}
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                {(summary.interest * 2).toFixed(0)}
              </td>
            </tr>
            <tr className="table-warning">
              <td style={{ padding: "6px 6px", fontWeight: "bold" }}>
                Closing Balance
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                --
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                <strong>{summary.closingBalance.toFixed(0)}</strong>
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                <strong>{summary.closingBalance.toFixed(0)}</strong>
              </td>
              <td className="text-center" style={{ padding: "6px 6px" }}>
                <strong>{summary.boardTotal.toFixed(0)}</strong>
              </td>
            </tr>
          </tbody>
        </Table>

        {/* Note Section - Compact */}
        <div className="bg-light border border-warning rounded p-3 mb-3">
          <span
            className="text-muted"
            style={{ fontSize: "12px", lineHeight: "1.3" }}
          >
            <strong>Note:</strong>
            <br />
            மேற்கூறிய விவரங்கள் எங்கள் ஆலுவலகத்தில் உள்ள ஆவணங்களைத் தழுவியும்
            மற்றும் எங்கள் மேலான பரிசீலனைக்கு உட்படுத்தப்பட்டதாகும். இருப்பினும்
            அதிகமாகக் கொடுக்கப்பட்ட வட்டி சந்தா மொத்தமாக கட்டிய நிலுவைத் தொகை
            இருப்பின் மறுபரிசீலனைக்கு உட்பட்டு இந்தத் தொகை மாறுபடும்.
            <br />
            <br />
            Deposits include the amounts relating to earlier years and brought
            to the account of the subscriber during this year. We hereby declare
            that the above mentioned information based on the records of this
            office is accurate and verified to the best of our knowledge.
            However these balances are subject to verification at the time of
            final payment and liable to revision after audit into rechecking of
            the account due to excess subscription (i.e subscription in excess
            of 10% of pay+ GP+DA), remittance slip error, excess interest or any
            other discrepancies.
          </span>
        </div>

        {/* Signature Section - Compact */}
        <div className="text-end" style={{ marginTop: "30px" }}>
          <div style={{ marginRight: "30px" }}>
            <div
              style={{
                borderTop: "1px solid #000",
                width: "150px",
                marginLeft: "auto",
                marginTop: "30px",
              }}
            ></div>
            <small style={{ fontSize: "10px" }}>For MEMBER SECRETARY</small>
          </div>
        </div>

        {/* Print Styles */}
        <style jsx>{`
          @media print {
            .statement-preview {
              margin: 0 !important;
              padding: 15px !important;
              font-size: 11px !important;
            }

            @page {
              size: A4;
              margin-top: 0.2in;
              margin-right: 0.2in;
              margin-bottom: 0.3in;
              margin-left: 0.3in;
            }

            table {
              page-break-inside: auto;
            }

            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }

            thead {
              display: table-header-group;
            }

            tfoot {
              display: table-footer-group;
            }
          }
        `}</style>
      </div>
    );
  };

  return (
    <div className="cps-generator">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .statement-preview, .statement-preview * {
              visibility: visible;
            }
            .statement-preview {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
          }
          
          .cps-generator {
            font-family: Arial, sans-serif;
          }
          
          .statement-preview {
            min-height: 297mm;
            width: 210mm;
            margin: 0 auto;
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
          
          .form-control:focus {
            border-color: #0d6efd;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
          }
          
          .btn-primary {
            background-color: #0d6efd;
            border-color: #0d6efd;
          }
          
          .btn-primary:hover {
            background-color: #0b5ed7;
            border-color: #0a58ca;
          }
          
          .table-responsive {
            border-radius: 0.375rem;
            overflow: hidden;
          }
          
          .alert {
            border-radius: 0.375rem;
          }
          
          .card {
            border-radius: 0.5rem;
            box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          }
          
          .card-header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
          }
        `}
      </style>

      <Container fluid className="py-4">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card className="shadow-sm">
              <Card.Header>
                <h4 className="mb-0">CPS Statement Generator</h4>
              </Card.Header>
              <Card.Body>
                {/* Employee Details Section */}
                <h5 className="mb-3">Employee Details</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={employeeDetails.name}
                        onChange={(e) =>
                          handleEmployeeDetailChange("name", e.target.value)
                        }
                        isInvalid={!!errors.name}
                        placeholder="Enter employee name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>CPS Account No *</Form.Label>
                      <Form.Control
                        type="text"
                        value={employeeDetails.cpsNo}
                        onChange={(e) =>
                          handleEmployeeDetailChange("cpsNo", e.target.value)
                        }
                        isInvalid={!!errors.cpsNo}
                        placeholder="Enter CPS account number"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.cpsNo}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Designation *</Form.Label>
                      <Form.Control
                        type="text"
                        value={employeeDetails.designation}
                        onChange={(e) =>
                          handleEmployeeDetailChange(
                            "designation",
                            e.target.value
                          )
                        }
                        isInvalid={!!errors.designation}
                        placeholder="Enter designation"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.designation}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Office Of</Form.Label>
                      <Form.Control
                        type="text"
                        value={employeeDetails.officeOf}
                        onChange={(e) =>
                          handleEmployeeDetailChange("officeOf", e.target.value)
                        }
                        placeholder="Enter office details"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth *</Form.Label>
                      <Form.Control
                        type="date"
                        value={employeeDetails.dateOfBirth}
                        onChange={(e) =>
                          handleEmployeeDetailChange(
                            "dateOfBirth",
                            e.target.value
                          )
                        }
                        isInvalid={!!errors.dateOfBirth}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.dateOfBirth}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Joining *</Form.Label>
                      <Form.Control
                        type="date"
                        value={employeeDetails.dateOfJoining}
                        onChange={(e) =>
                          handleEmployeeDetailChange(
                            "dateOfJoining",
                            e.target.value
                          )
                        }
                        isInvalid={!!errors.dateOfJoining}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.dateOfJoining}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Opening Balance Section */}
                <hr />
                <h5 className="mb-3">Opening Balance</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contribution</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={openingBalance.contribution}
                        onChange={(e) =>
                          setOpeningBalance((prev) => ({
                            ...prev,
                            contribution: Math.max(
                              0,
                              parseFloat(e.target.value) || 0
                            ),
                          }))
                        }
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Arrear</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={openingBalance.arrear}
                        onChange={(e) =>
                          setOpeningBalance((prev) => ({
                            ...prev,
                            arrear: Math.max(
                              0,
                              parseFloat(e.target.value) || 0
                            ),
                          }))
                        }
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Others</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={openingBalance.others}
                        onChange={(e) =>
                          setOpeningBalance((prev) => ({
                            ...prev,
                            others: Math.max(
                              0,
                              parseFloat(e.target.value) || 0
                            ),
                          }))
                        }
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Contributions Section */}
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Monthly Contributions</h5>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => setShowRangeModal(true)}
                    >
                      <Calendar className="me-1" size={16} />
                      Add Range
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleClear()}
                    >
                      <Trash2 className="me-1" size={16} />
                      Clear
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="me-2 d-none"
                      disabled={contributions.length < 12}
                      onClick={() => setShowInterestModal(true)}
                    >
                      <Percent className="me-1" size={16} />
                      Bulk Interest
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={contributions.length ===12}
                      onClick={addContribution}
                    >
                      <Plus className="me-1" size={16} />
                      Add Month
                    </Button>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead className="table-dark">
                      <tr>
                        <th>Month</th>
                        <th>Year</th>
                        <th>Contribution</th>
                        <th>Arrear</th>
                        <th>Others</th>
                        <th>Total</th>
                        <th>Interest Rate (%)</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contributions.map((contrib, index) => (
                        <tr key={contrib.id}>
                          <td>
                            <Form.Select
                              size="sm"
                              value={contrib.month}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "month",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`month_${index}`]}
                            >
                              <option value="">Select Month</option>
                              {months.map((month) => (
                                <option key={month} value={month}>
                                  {month}
                                </option>
                              ))}
                            </Form.Select>
                            {errors[`month_${index}`] && (
                              <small className="text-danger">
                                {errors[`month_${index}`]}
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="2000"
                              max="2100"
                              value={contrib.year}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "year",
                                  parseInt(e.target.value) ||
                                  new Date().getFullYear()
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              step="0.01"
                              value={contrib.contribution}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "contribution",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`contribution_${index}`]}
                            />
                            {errors[`contribution_${index}`] && (
                              <small className="text-danger">
                                {errors[`contribution_${index}`]}
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              step="0.01"
                              value={contrib.arrear}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "arrear",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`arrear_${index}`]}
                            />
                            {errors[`arrear_${index}`] && (
                              <small className="text-danger">
                                {errors[`arrear_${index}`]}
                              </small>
                            )}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              step="0.01"
                              value={contrib.others}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "others",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`others_${index}`]}
                            />
                            {errors[`others_${index}`] && (
                              <small className="text-danger">
                                {errors[`others_${index}`]}
                              </small>
                            )}
                          </td>
                          <td className="fw-bold">
                            {calculateRowTotal(contrib).toFixed(2)}
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              size="sm"
                              min="0"
                              max="100"
                              step="0.1"
                              value={contrib.interestRate}
                              onChange={(e) =>
                                handleContributionChange(
                                  contrib.id,
                                  "interestRate",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors[`interestRate_${index}`]}
                            />
                            {errors[`interestRate_${index}`] && (
                              <small className="text-danger">
                                {errors[`interestRate_${index}`]}
                              </small>
                            )}
                          </td>
                          <td>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeContribution(contrib.id)}
                              disabled={contributions.length === 1}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-secondary">
                      <tr>
                        <td colSpan="2" className="fw-bold">
                          Total
                        </td>
                        <td className="fw-bold">
                          {calculateGrandTotals().contribution.toFixed(2)}
                        </td>
                        <td className="fw-bold">
                          {calculateGrandTotals().arrear.toFixed(2)}
                        </td>
                        <td className="fw-bold">
                          {calculateGrandTotals().others.toFixed(2)}
                        </td>
                        <td className="fw-bold">
                          {calculateGrandTotals().total.toFixed(2)}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>

                {/* Action Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <div>
                    <Button
                      variant="success"
                      className="me-2"
                      disabled={contributions.length === 1}
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      <Eye className="me-1" size={16} />
                      {showPreview ? "Hide Preview" : "Show Preview"}
                    </Button>
                    <Button variant="info" onClick={calculateSummary}>
                      <Calculator className="me-1" size={16} />
                      Calculate
                    </Button>
                  </div>
                  <Button variant="primary" onClick={handlePrint}>
                    <Download className="me-1" size={16} />
                    Print Statement
                  </Button>
                </div>

                {/* Summary Display */}
                {showPreview && (
                  <Alert variant="info" className="mt-3">
                    <h6>Summary</h6>
                    <Row>
                      <Col md={3}>
                        <strong>Opening Balance:</strong> ₹
                        {calculateSummary().openingBalance.toLocaleString()}
                      </Col>
                      <Col md={3}>
                        <strong>Total Deposits:</strong> ₹
                        {calculateSummary().deposits.toLocaleString()}
                      </Col>
                      <Col md={3}>
                        <strong>Interest Earned:</strong> ₹
                        {calculateSummary().interest.toLocaleString()}
                      </Col>
                      <Col md={3}>
                        <strong>Closing Balance:</strong> ₹
                        {calculateSummary().closingBalance.toLocaleString()}
                      </Col>
                    </Row>
                  </Alert>
                )}

                {/* Statement Preview */}
                {showPreview && <StatementPreview />}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* Month Range Modal */}
        <Modal
          show={showRangeModal}
          onHide={() => setShowRangeModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Month Range</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Month</Form.Label>
                  <Form.Select
                    value={rangeData.fromMonth}
                    onChange={(e) => {
                      const newFromMonth = e.target.value;
                      const fromMonthIndex = months.indexOf(newFromMonth);
                      const toMonthIndex = (fromMonthIndex + 11) % 12;
                      const newToMonth = months[toMonthIndex];

                      setRangeData((prev) => ({
                        ...prev,
                        fromMonth: newFromMonth,
                        toMonth: newToMonth,
                      }));
                    }}
                  >
                    <option value="">Select Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Year</Form.Label>
                  <Form.Control
                    type="number"
                    min="2000"
                    max="2100"
                    value={rangeData.fromYear}
                    onChange={(e) => {
                      const newFromYear = parseInt(e.target.value) || new Date().getFullYear();
                      setRangeData((prev) => ({
                        ...prev,
                        fromYear: newFromYear,
                        toYear: newFromYear + 1,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Month</Form.Label>
                  <Form.Select
                    value={rangeData.toMonth}
                    onChange={(e) => {
                      const newToMonth = e.target.value;
                      const toMonthIndex = months.indexOf(newToMonth);
                      const fromMonthIndex = (toMonthIndex + 1) % 12;
                      const newFromMonth = months[fromMonthIndex];

                      setRangeData((prev) => ({
                        ...prev,
                        fromMonth: newFromMonth,
                        toMonth: newToMonth,
                      }));
                    }}
                  >
                    <option value="">Select Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Year</Form.Label>
                  <Form.Control
                    type="number"
                    min="2000"
                    max="2100"
                    value={rangeData.toYear}
                    onChange={(e) => {
                      const newToYear = parseInt(e.target.value) || new Date().getFullYear();
                      setRangeData((prev) => ({
                        ...prev,
                        fromYear: newToYear - 1, // Auto adjust fromYear to toYear - 1
                        toYear: newToYear,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Monthly Contribution</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={rangeData.monthlyContribution}
                    onChange={(e) =>
                      setRangeData((prev) => ({
                        ...prev,
                        monthlyContribution: Math.max(
                          0,
                          parseFloat(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Monthly Arrear</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={rangeData.monthlyArrear}
                    onChange={(e) =>
                      setRangeData((prev) => ({
                        ...prev,
                        monthlyArrear: Math.max(
                          0,
                          parseFloat(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Monthly Others</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={rangeData.monthlyOthers}
                    onChange={(e) =>
                      setRangeData((prev) => ({
                        ...prev,
                        monthlyOthers: Math.max(
                          0,
                          parseFloat(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Interest Rate (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={rangeData.interestRate}
                    onChange={(e) =>
                      setRangeData((prev) => ({
                        ...prev,
                        interestRate: Math.max(
                          0,
                          parseFloat(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowRangeModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={generateMonthRange}>
              Generate Range
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Bulk Interest Rate Modal */}
        <Modal
          show={showInterestModal}
          onHide={() => setShowInterestModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Apply Bulk Interest Rate</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Month</Form.Label>
                  <Form.Select
                    value={bulkInterestData.fromMonth}
                    onChange={(e) => {
                      const newFromMonth = e.target.value;
                      const fromMonthIndex = months.indexOf(newFromMonth);
                      const toMonthIndex = (fromMonthIndex + 11) % 12;
                      const newToMonth = months[toMonthIndex];

                      setBulkInterestData((prev) => ({
                        ...prev,
                        fromMonth: newFromMonth,
                        toMonth: newToMonth,
                      }));
                    }}
                  >
                    <option value="">Select Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>From Year</Form.Label>
                  <Form.Control
                    type="number"
                    min="2000"
                    max="2100"
                    value={bulkInterestData.fromYear}
                    onChange={(e) => {
                      const newFromYear = parseInt(e.target.value) || new Date().getFullYear();
                      setBulkInterestData((prev) => ({
                        ...prev,
                        fromYear: newFromYear,
                        toYear: newFromYear + 1,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Month</Form.Label>
                  <Form.Select
                    value={bulkInterestData.toMonth}
                    onChange={(e) => {
                      const newToMonth = e.target.value;
                      const toMonthIndex = months.indexOf(newToMonth);
                      // From month should be 1 month after to month for 12-month range
                      const fromMonthIndex = (toMonthIndex + 1) % 12;
                      const newFromMonth = months[fromMonthIndex];

                      setBulkInterestData((prev) => ({
                        ...prev,
                        fromMonth: newFromMonth,
                        toMonth: newToMonth,
                      }));
                    }}
                  >
                    <option value="">Select Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>To Year</Form.Label>
                  <Form.Control
                    type="number"
                    min="2000"
                    max="2100"
                    value={bulkInterestData.toYear}
                    onChange={(e) => {
                      const newToYear = parseInt(e.target.value) || new Date().getFullYear();
                      setBulkInterestData((prev) => ({
                        ...prev,
                        fromYear: newToYear - 1, // Auto adjust fromYear to toYear - 1
                        toYear: newToYear,
                      }));
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Interest Rate (% per annum)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={bulkInterestData.interestRate}
                    onChange={(e) =>
                      setBulkInterestData((prev) => ({
                        ...prev,
                        interestRate: Math.max(
                          0,
                          parseFloat(e.target.value) || 0
                        ),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowInterestModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={applyBulkInterestRate}>
              Apply Interest Rate
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default CPSStatementGenerator;
